import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import { Box, Button, Chip, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ActivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import introImage from '../asset/intro-hero.jpg';
import { VerbaScholaPublicFooter, VerbaScholaPublicHeader } from '../component/VerbaScholaPublicChrome';
import { useSignedInAccountState } from '../component/verbaScholaLanding';
import { isJwtLoggedIn } from '../util/session';
import {
    BLUE, BLUE_TEXT, BORDER, ERROR_SOFT, ERROR_TEXT, INK, INTER, NAVY,
    ON_NAVY_LABEL, ON_NAVY_TEXT, PAGE_BACKGROUND, POPPINS, PROGRESS_TRACK, SLATE,
    SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_SUBTLE, WARNING_SOFT, WARNING_TEXT, YELLOW,
    PILOT_REQUEST_HREF, PUBLIC_CONTENT_MAX_WIDTH, PUBLIC_PAGE_GUTTER, SUPPORT_EMAIL,
    publicCardSx as cardSx, publicSectionLeadSx as sectionLeadSx, publicSectionTitleSx as sectionTitleSx, primaryButtonSx,
} from '../style/verbaScholaPublicStyles';

const sectionSx = { maxWidth: PUBLIC_CONTENT_MAX_WIDTH, mx: 'auto', px: PUBLIC_PAGE_GUTTER } as const;
const ctaButtonSx = { ...primaryButtonSx, minHeight: 48, px: 3 } as const;

function HeroFeedbackCard() {
    // A static glimpse of the real speaking-result language, not a live score.
    return (
        <Box aria-hidden sx={{
            ...cardSx, position: 'absolute', left: { xs: 12, sm: -24 }, bottom: { xs: 12, sm: 28 },
            width: { xs: 'calc(100% - 24px)', sm: 260 }, p: 2,
            boxShadow: '0 12px 32px rgba(22, 58, 99, 0.16)',
        }}>
            <Typography sx={{ fontFamily: INTER, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: SLATE }}>
                Speaking check
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                <Box>
                    <Typography sx={{ fontFamily: POPPINS, fontSize: 24, fontWeight: 600, color: NAVY, lineHeight: 1.2 }}>我们去吃饭吧</Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE }}>Let&apos;s go eat.</Typography>
                </Box>
                <Chip icon={<CheckCircleOutlineRoundedIcon />} label="Good" size="small"
                    sx={{ fontWeight: 700, color: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT, '& .MuiChip-icon': { color: SUCCESS_TEXT } }} />
            </Stack>
        </Box>
    );
}

function Hero() {
    return (
        <Box component="section" aria-labelledby="home-hero-title" sx={{ backgroundColor: PAGE_BACKGROUND }}>
            <Box sx={{ ...sectionSx, py: { xs: 5, md: 9 } }}><Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, fontWeight: 700, color: BLUE_TEXT, mb: 1.5 }}>
                        Speaking practice for language classrooms
                    </Typography>
                    <Typography id="home-hero-title" component="h1" sx={{
                        fontFamily: POPPINS, fontWeight: 600, color: NAVY,
                        fontSize: { xs: 32, sm: 38, md: 44 }, lineHeight: 1.15, letterSpacing: '-0.01em',
                    }}>
                        Get students speaking with <Box component="span" sx={{ color: BLUE_TEXT }}>confidence</Box>
                    </Typography>
                    <Typography sx={{ ...sectionLeadSx, mt: 2, maxWidth: 520 }}>
                        Build practice from your curriculum. Students speak and get feedback outside class.
                        See who needs your attention before the next lesson.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                        <Button component={RouterLink} to={PILOT_REQUEST_HREF} sx={ctaButtonSx}>Request a pilot</Button>
                        <Button component={RouterLink} to="/login" variant="outlined" sx={{ minHeight: 48, px: 3, borderColor: BORDER, backgroundColor: '#FFFFFF' }}>
                            Log in
                        </Button>
                    </Stack>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 2 }}>
                        Students: use the invitation email or class link from your teacher to set up your account.
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative', ml: { md: 3 } }}>
                        <Box component="img" src={introImage}
                            alt="A smiling teacher in front of a classroom of students working at their desks"
                            sx={{
                                display: 'block', width: '100%', aspectRatio: { xs: '4 / 3', md: '5 / 4' },
                                objectFit: 'cover', objectPosition: '50% 20%', borderRadius: '20px',
                            }} />
                        <HeroFeedbackCard />
                    </Box>
                </Grid>
            </Grid></Box>
        </Box>
    );
}

function GlimpseCard({ step, title, text, children }: { step: string; title: string; text: string; children: ReactNode }) {
    return (
        <Box sx={{ ...cardSx, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box aria-hidden sx={{ backgroundColor: SURFACE_SUBTLE, borderBottom: `1px solid ${BORDER}`, p: 2.5, minHeight: 176, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {children}
            </Box>
            <Box sx={{ p: 3 }}>
                <Typography sx={{ fontFamily: INTER, fontSize: 12, fontWeight: 700, color: BLUE_TEXT }}>{step}</Typography>
                <Typography component="h3" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY, mt: 0.75 }}>{title}</Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 15, lineHeight: 1.6, color: SLATE, mt: 1 }}>{text}</Typography>
            </Box>
        </Box>
    );
}

const miniLabelSx = { fontFamily: INTER, fontSize: 12, fontWeight: 600, color: SLATE } as const;

function LessonGlimpse() {
    const steps = [{ label: 'Description', done: true }, { label: 'Lesson plan', done: true }, { label: 'Exercises', done: false }];
    return (
        <Stack spacing={1.25}>
            {steps.map((step, index) => (
                <Stack key={step.label} direction="row" alignItems="center" spacing={1.25} sx={{ backgroundColor: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: '12px', px: 1.5, py: 1 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: INTER, fontSize: 12, fontWeight: 700,
                        backgroundColor: step.done ? SUCCESS_SOFT : SOFT_BLUE, color: step.done ? SUCCESS_TEXT : BLUE_TEXT }}>
                        {step.done ? <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} /> : index + 1}
                    </Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, fontWeight: 600, color: NAVY }}>{step.label}</Typography>
                    {!step.done && <Chip icon={<AutoAwesomeOutlinedIcon />} label="Draft ready" size="small"
                        sx={{ ml: 'auto !important', height: 24, fontSize: 11, fontWeight: 700, color: INK, backgroundColor: YELLOW, '& .MuiChip-icon': { color: INK, fontSize: 14 } }} />}
                </Stack>
            ))}
        </Stack>
    );
}

function SpeakingGlimpse() {
    const grades = [
        { syllable: '我', label: 'Good', color: SUCCESS_TEXT, bg: SUCCESS_SOFT },
        { syllable: '们', label: 'Close', color: WARNING_TEXT, bg: WARNING_SOFT },
        { syllable: '去', label: 'Good', color: SUCCESS_TEXT, bg: SUCCESS_SOFT },
        { syllable: '吃', label: 'Needs practice', color: ERROR_TEXT, bg: ERROR_SOFT },
    ];
    return (
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
            {grades.map(grade => (
                <Stack key={grade.syllable} alignItems="center" spacing={0.75} sx={{ backgroundColor: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: '14px', px: 1.25, py: 1.25, minWidth: 64 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontSize: 26, fontWeight: 600, color: NAVY, lineHeight: 1.1 }}>{grade.syllable}</Typography>
                    <Box sx={{ fontFamily: INTER, fontSize: 11, fontWeight: 700, color: grade.color, backgroundColor: grade.bg, borderRadius: 99, px: 1, py: 0.25, whiteSpace: 'nowrap' }}>{grade.label}</Box>
                </Stack>
            ))}
        </Stack>
    );
}

function ProgressGlimpse() {
    return (
        <Stack spacing={1.5} sx={{ backgroundColor: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: '14px', p: 2 }}>
            <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontFamily: INTER, fontSize: 13, fontWeight: 700, color: NAVY }}>Ordering food</Typography>
                <Typography sx={miniLabelSx}>18 of 24 completed</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={75} sx={{ height: 8, borderRadius: 99, backgroundColor: PROGRESS_TRACK, '& .MuiLinearProgress-bar': { backgroundColor: BLUE, borderRadius: 99 } }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Box sx={{ fontFamily: INTER, fontSize: 12, fontWeight: 700, color: ERROR_TEXT, backgroundColor: ERROR_SOFT, borderRadius: 99, px: 1.25, py: 0.5 }}>4 have not started</Box>
                <Box sx={{ fontFamily: INTER, fontSize: 12, fontWeight: 700, color: BLUE_TEXT, backgroundColor: SOFT_BLUE, borderRadius: 99, px: 1.25, py: 0.5 }}>3 to review</Box>
            </Stack>
        </Stack>
    );
}

function HowItWorks() {
    return (
        <Box component="section" aria-labelledby="home-platform-title" sx={{ backgroundColor: '#FFFFFF', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
            <Box sx={{ ...sectionSx, py: { xs: 6, md: 9 } }}>
                <Typography id="home-platform-title" component="h2" sx={{ ...sectionTitleSx, textAlign: { md: 'center' } }}>From lesson plan to useful feedback</Typography>
                <Typography sx={{ ...sectionLeadSx, mt: 1.5, textAlign: { md: 'center' }, maxWidth: 620, mx: { md: 'auto' } }}>
                    One classroom workflow, with a clear next step for teachers and students.
                </Typography>
                <Grid container spacing={2.5} sx={{ mt: 3 }}>
                    <Grid item xs={12} md={4}>
                        <GlimpseCard step="1 · Teachers plan" title="Build from your curriculum" text="Set your goals, vocabulary, and grammar. Review the lesson draft before students see it.">
                            <LessonGlimpse />
                        </GlimpseCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <GlimpseCard step="2 · Students practice" title="Speak and get feedback" text="Students listen, record, and see which sounds and tones need more practice.">
                            <SpeakingGlimpse />
                        </GlimpseCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <GlimpseCard step="3 · Teachers follow up" title="See what needs attention" text="Check completion and speaking work before your next class.">
                            <ProgressGlimpse />
                        </GlimpseCard>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

function Evidence() {
    // Sources (see doc/SiteOverview.md):
    // - Cross-pilot figures: V3–V5 "Early metrics", repeated in V14 and V15. Grading hours are omitted
    //   because the decks disagree (~20 h in V3–V5, a 5–10 h target in V14, a result in V15).
    // - Grade 7 case study: doc/pilot_ms54_activity_analysis.md. Its AI-agreement figures (sampled from the
    //   whole database, mostly founder-graded) and +18.2 improvement (best-of-many bias) are deliberately left out.
    // - Quotes: verbatim excerpts of the full remarks attributed in V3–V5; ellipses mark omissions.
    const metrics = [
        { value: '25', label: 'sentences per student each week' },
        { value: '80%', label: 'assignment completion rate' },
        { value: '119', label: 'sentences per student across a pilot' },
    ];
    const caseStudy = [
        { value: '2.6%', label: 'of 3,601 recordings had a human review entry' },
        { value: '62.6%', label: 'of 3,598 recordings in the repeat-practice analysis followed a passing result on that sentence' },
    ];
    const quotes = [
        {
            text: 'Extremely helpful for teachers of Chinese as a foreign language… particularly powerful in developing speaking proficiency… allowing them to clearly identify areas for improvement and make meaningful progress in their oral Chinese.',
            who: 'Ms. Sun, pilot teacher',
        },
        {
            text: 'Like a supplemental TA inside the classroom… having real-time feedback on tones and pronunciation is extremely helpful… teachers often don’t have time to provide one-on-one correction and reinforcement.',
            who: 'Ms. Ji, pilot teacher',
        },
    ];
    const statValueSx = { fontFamily: POPPINS, fontWeight: 600, color: '#FFFFFF', lineHeight: 1.1 } as const;
    return (
        <Box component="section" aria-labelledby="home-evidence-title" sx={{ ...sectionSx, py: { xs: 6, md: 9 } }}>
            <Box sx={{ backgroundColor: NAVY, borderRadius: '20px', p: { xs: 3, md: 6 } }}>
                <Typography id="home-evidence-title" component="h2" sx={{ ...sectionTitleSx, color: '#FFFFFF' }}>What classroom pilots showed</Typography>
                <Typography sx={{ ...sectionLeadSx, color: ON_NAVY_TEXT, mt: 1.5, maxWidth: 640 }}>
                    Early classroom pilot figures:
                </Typography>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {metrics.map(metric => (
                        <Grid item xs={12} sm={4} key={metric.value}>
                            <Typography sx={{ ...statValueSx, fontSize: { xs: 36, md: 44 } }}>{metric.value}</Typography>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 15, color: ON_NAVY_TEXT, mt: 0.5 }}>{metric.label}</Typography>
                        </Grid>
                    ))}
                </Grid>
                <Box sx={{ mt: 4, p: { xs: 2.5, md: 3 }, borderRadius: '16px', border: '1px solid rgba(255,255,255,0.14)' }}>
                    <Typography component="h3" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: '#FFFFFF' }}>
                        Grade 7 Mandarin class, spring 2025
                    </Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 14, color: ON_NAVY_TEXT, mt: 0.5 }}>
                        42 students, one teacher, one unit of speaking homework graded on completion.
                    </Typography>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        {caseStudy.map(stat => (
                            <Grid item xs={12} sm={6} key={stat.value}>
                                <Typography sx={{ ...statValueSx, fontSize: { xs: 30, md: 34 } }}>{stat.value}</Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 14, lineHeight: 1.5, color: ON_NAVY_TEXT, mt: 0.5 }}>{stat.label}</Typography>
                            </Grid>
                        ))}
                    </Grid>
                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: ON_NAVY_TEXT, mt: 2 }}>
                        Reported internal analysis; the original calculation has not been independently reproduced.
                    </Typography>
                </Box>
                <Grid container spacing={2.5} sx={{ mt: 3 }}>
                    {quotes.map(quote => (
                        <Grid item xs={12} md={6} key={quote.who}>
                            <Box component="figure" sx={{ m: 0, height: '100%', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '16px', p: 3 }}>
                                <FormatQuoteRoundedIcon aria-hidden sx={{ color: ON_NAVY_LABEL, fontSize: 28 }} />
                                <Box component="blockquote" sx={{ m: 0, mt: 1, fontFamily: INTER, fontSize: 15, lineHeight: 1.65, color: '#FFFFFF' }}>
                                    “{quote.text}”
                                </Box>
                                <Box component="figcaption" sx={{ mt: 2, fontFamily: INTER, fontSize: 13, fontWeight: 700, color: ON_NAVY_TEXT }}>{quote.who}</Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
}

function ClosingCta() {
    return (
        <Box component="section" aria-labelledby="home-cta-title" sx={{ ...sectionSx, pb: { xs: 6, md: 9 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} sx={{ ...cardSx, p: { xs: 3, md: 5 } }}>
                <Box sx={{ flex: 1 }}>
                    <Typography id="home-cta-title" component="h2" sx={{ ...sectionTitleSx, fontSize: { xs: 22, md: 26 } }}>Ready to try VerbaSchola with your class?</Typography>
                    <Typography sx={{ ...sectionLeadSx, mt: 1 }}>
                        We&apos;ll set up a short demo and a teacher account for your class. Questions first? Email <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: BLUE_TEXT, fontWeight: 600 }}>{SUPPORT_EMAIL}</Box>.
                    </Typography>
                </Box>
                <Button component={RouterLink} to={PILOT_REQUEST_HREF} sx={{ ...ctaButtonSx, alignSelf: { xs: 'stretch', md: 'center' } }}>Request a pilot</Button>
            </Stack>
        </Box>
    );
}

export default function Home() {
    useUserActivityTracker(ActivityEnum.Home, null, null, isJwtLoggedIn());
    const { account, unavailable, retry } = useSignedInAccountState();

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: PAGE_BACKGROUND }}>
            <VerbaScholaPublicHeader account={account} accountPending={isJwtLoggedIn() && !account}
                accountUnavailable={unavailable} onRetryAccount={retry} />
            <Box component="main" sx={{ flex: 1 }}>
                <Hero />
                <HowItWorks />
                <Evidence />
                <ClosingCta />
            </Box>
            <VerbaScholaPublicFooter />
        </Box>
    );
}
